// game/event-log.js: Phase 3 #10 pure event-log formatter + accumulator.
//
// The networked client already reduces a stream of game events
// (CARD_PLAYED, DEALER_DISCARDED, BID_SUBMITTED, TRICK_RESOLVED,
// HAND_SCORED, see hooks/useGameWs.js GAME_EVENT_TYPES). This module
// turns each event into ONE readable line for the side log and
// accumulates those lines, oldest → newest, capped to a sane length.
//
// Pure (no DOM / preact / engine) so node:test can cover it directly,
// the EventLogPanel JSX is exercised by client/e2e/event-log.spec.js.
//
// Card naming + the bower suffix reuse the existing helpers (isBower +
// the SUIT_SYMBOL/SUIT_NAME glyph map) so the log reads identically to
// the end-of-hand reveal, we don't reinvent card-to-text.

import { isBower, _internals as cardInternals } from '../canvas/cards.js';
import { handSummaryFromSnapshot } from '../ui/hand-summary.js';
import { headlineFor } from '../ui/end-of-hand.js';
import { plainSeatLabelsFromSnapshot } from '../views/seat-labels.js';

const { SUIT_SYMBOL, SUIT_NAME } = cardInternals;

// Keep the last N lines. A full hand is ~25 lines (20 plays + 5 tricks +
// bids/scores), so 100 holds roughly the last four hands of context
// without unbounded growth on a long game.
export const EVENT_LOG_CAP = 100;

/**
 * "K♣", "9♦ (left bower)". Rank uses the card glyph (T → "10" to match
 * the on-canvas rank), suit uses the colored glyph; the bower suffix is
 * the same "(right bower)" / "(left bower)" wording as the reveal modal.
 *
 * @param {string|null|undefined} cardId
 * @param {string|null|undefined} trumpSuit
 * @returns {string}
 */
function cardText(cardId, trumpSuit) {
  if (!cardId || cardId.length !== 2) return 'a card';
  const rank = cardId[0] === 'T' ? '10' : cardId[0];
  const glyph = SUIT_SYMBOL[cardId[1]] || '?';
  const bower = isBower(cardId, trumpSuit);
  const suffix = bower === 'right' ? ' (right bower)'
               : bower === 'left'  ? ' (left bower)'
               : '';
  return `${rank}${glyph}${suffix}`;
}

/**
 * "spades", "clubs" lowercase suit word for the trump-called line.
 * @param {string|null|undefined} suit
 * @returns {string}
 */
function suitWord(suit) {
  const name = SUIT_NAME[suit];
  return name ? name.toLowerCase() : 'trump';
}

/**
 * Subject + verb agreement for the play line: the local player reads
 * "You play", everyone else "<name> plays". Same trick for discards.
 *
 * @param {string} label
 * @param {string} youVerb   e.g. "play"
 * @param {string} theyVerb  e.g. "plays"
 * @returns {string}
 */
function subjectVerb(label, youVerb, theyVerb) {
  return label === 'You' ? `You ${youVerb}` : `${label} ${theyVerb}`;
}

/**
 * Format a single game event into one readable log line, or null when
 * the event should not appear in the log (timer/infra events, unknown
 * types). Passes ARE logged (B4a-A7), round one and round two.
 *
 * @param {{ type?: string, payload?: any }|null|undefined} event
 * @param {{ trumpSuit?: string|null, seatLabel: (luaSeat:number) => string, myTeam?: 'team_a'|'team_b', autoFromTimeout?: boolean }} ctx
 * @returns {string|null}
 */
export function formatGameEvent(event, ctx) {
  if (!event || typeof event !== 'object' || !event.type) return null;
  const p = event.payload || {};
  // Prefer the event's own snapshot trump (the suit is final at play
  // time even if the ctx hasn't caught up); fall back to ctx.
  const trumpSuit = p.game_state_snapshot?.trump_suit ?? ctx?.trumpSuit ?? null;
  const labelOf = (seat) => (seat != null ? ctx.seatLabel(seat) : 'Someone');

  switch (event.type) {
    case 'BOT_SUBSTITUTED': {
      // 'leave' is the client's own reason: the server says 'disconnect'
      // for a leave, and the reducer knows the player left the room.
      const reason = { timeout: 'timed out', disconnect: 'disconnected', leave: 'left' }[p.reason];
      const who = labelOf(p.actor_seat);
      const name = who === 'You' ? 'you' : who;
      return reason ? `A bot plays for ${name} (${reason})` : `A bot plays for ${name}`;
    }
    case 'SEAT_RECLAIMED':
      return `${labelOf(p.actor_seat)} is back`;
    case 'SEAT_DEPARTED':
      return `${labelOf(p.actor_seat)} left`;
    case 'CARD_PLAYED': {
      const who = labelOf(p.actor_seat);
      if (ctx?.autoFromTimeout) {
        return `${subjectVerb(who, 'ran out of time; auto-played', 'ran out of time; auto-played')} the ${cardText(p.card_id, trumpSuit)}`;
      }
      return `${subjectVerb(who, 'play', 'plays')} the ${cardText(p.card_id, trumpSuit)}`;
    }
    case 'DEALER_DISCARDED': {
      const who = labelOf(p.actor_seat);
      const card = p.card_id ? ` the ${cardText(p.card_id, trumpSuit)}` : ' a card';
      return `${subjectVerb(who, 'discard', 'discards')}${card}`;
    }
    case 'BID_SUBMITTED': {
      const who = labelOf(p.actor_seat);
      // B2-9: a bid the server placed after the seat timed out reads as
      // an auto-call, never as a deliberate bid.
      if (p.auto_from_timeout) {
        if (!p.action || p.action === 'pass') {
          return `${subjectVerb(who, 'ran out of time; auto-passed', 'ran out of time; auto-passed')}`;
        }
        const glyph = SUIT_SYMBOL[p.suit ?? trumpSuit] || suitWord(p.suit ?? trumpSuit);
        const alone = p.action === 'go_alone'
          ? (who === 'You' ? ' and are going alone' : ' and is going alone')
          : '';
        return `${subjectVerb(who, 'ran out of time; auto-called', 'ran out of time; auto-called')} ${glyph}${alone}`;
      }
      // B4a-A7: each pass is logged, round one and round two.
      if (!p.action || p.action === 'pass') {
        return subjectVerb(who, 'passed', 'passed');
      }
      const suit = suitWord(trumpSuit ?? p.suit);
      if (p.action === 'go_alone') {
        return subjectVerb(who, `called ${suit} and are going alone`, `called ${suit} and is going alone`);
      }
      if (p.action === 'order_up') {
        return `${who === 'You' ? 'You' : who} ordered up — ${suit} is trump`;
      }
      // call_suit (round 2) and any other non-pass naming bid.
      return `${who} called ${suit}`;
    }
    case 'TRICK_RESOLVED': {
      const who = labelOf(p.winner_seat);
      return `${subjectVerb(who, 'win', 'wins')} the trick`;
    }
    case 'HAND_SCORED': {
      const snap = p.game_state_snapshot ?? null;
      const scores = snap?.scores;
      // T19: the log reads from the viewer, US/THEM, not Team A/B.
      // ctx.myTeam is 'team_b' for seats 2/4; default team_a (hot-seat).
      const mine = ctx.myTeam === 'team_b' ? 'team_b' : 'team_a';
      const theirs = mine === 'team_a' ? 'team_b' : 'team_a';
      const tail = scores
        ? ` (US ${scores[mine] ?? 0}, THEM ${scores[theirs] ?? 0})`
        : '';
      // B4a-A3: the line reads from the viewer's side, exactly like the
      // recap headline ("We made it — US +1", "They made it — THEM +1",
      // "Euchred them! US +2"), never from the maker's point of view.
      // Without a score summary (old snapshot) keep the server label.
      const summary = snap ? handSummaryFromSnapshot(snap) : null;
      const headline = summary ? headlineFor(summary, mine) : '';
      const label = headline || p.scoring_label || 'hand complete';
      return `Hand scored: ${label}${tail}`;
    }
    case 'HAND_PASSED_OUT': {
      return 'Everyone passed; new deal';
    }
    default:
      return null;
  }
}

/**
 * Append a formatted line to the ordered log, dropping the oldest once
 * the cap is hit. Null/empty lines (skipped events) are ignored. Pure,
 * returns a NEW array, never mutates the input.
 *
 * @param {string[]} log
 * @param {string|null|undefined} line
 * @param {number} [cap]
 * @returns {string[]}
 */
export function appendEvent(log, line, cap = EVENT_LOG_CAP) {
  const base = Array.isArray(log) ? log : [];
  if (!line) return base === log ? base : [...base];
  const next = [...base, line];
  if (next.length > cap) next.splice(0, next.length - cap);
  return next;
}

/**
 * Normalize a Lua-table-as-object (or array) into a real array.
 * parity.serialize emits every Lua table as a JSON object keyed by
 * stringified integers, so reattach snapshots may carry either shape.
 *
 * @param {any} raw
 * @returns {any[]}
 */
function asList(raw) {
  if (!raw) return [];
  return Array.isArray(raw) ? raw : Object.values(raw);
}

/**
 * F4: rebuild the current hand's log lines from a reattach snapshot.
 *
 * A reload (or reattach) mid-hand arrives as a single GAME_INITIALIZED
 * whose game_state_snapshot holds the whole hand so far: bid_history
 * (every bid in order, seat + action + suit), trump_suit/maker_seat
 * (the trump call, read off the naming bid), completed_tricks (each
 * with its plays + winner_seat) and trick_plays (the in-progress
 * trick). Each entry is formatted through formatGameEvent, so rebuilt
 * lines read exactly like live ones.
 *
 * What the snapshot does NOT hold is never invented: the dealer's
 * discarded card is stripped server-side (never in any snapshot, not
 * even the dealer's own), so no discard line is rebuilt — a reload
 * during the discard shows the bids and the trump call, and the next
 * live DEALER_DISCARDED appends as usual.
 *
 * @param {any} snap  a game_state_snapshot from a reattach GAME_INITIALIZED
 * @param {0|1|2|3|null} mySeatIndex  the viewer's seat (your_seat_index)
 * @returns {string[]}
 */
export function rebuildLogFromSnapshot(snap, mySeatIndex) {
  if (!snap || typeof snap !== 'object') return [];
  const labels = plainSeatLabelsFromSnapshot(snap, mySeatIndex);
  const myLua = mySeatIndex != null ? mySeatIndex + 1 : 1;
  const ctx = {
    trumpSuit: snap.trump_suit ?? null,
    seatLabel: (luaSeat) => labels[luaSeat] || `Seat ${luaSeat}`,
    myTeam: /** @type {'team_a'|'team_b'} */ ((myLua === 1 || myLua === 3) ? 'team_a' : 'team_b'),
  };
  /** @type {string[]} */
  let log = [];
  // Snapshots omit timeout flags for old bids and completed plays.
  // Rebuilt lines cannot tell a human move from a timer-made move.
  for (const bid of asList(snap.bid_history)) {
    if (!bid || typeof bid !== 'object') continue;
    log = appendEvent(log, formatGameEvent({
      type: 'BID_SUBMITTED',
      payload: {
        actor_seat: bid.seat, action: bid.action, suit: bid.suit ?? null,
        game_state_snapshot: snap,
      },
    }, ctx));
  }
  for (const trick of asList(snap.completed_tricks)) {
    if (!trick || typeof trick !== 'object') continue;
    for (const play of asList(trick.plays)) {
      if (!play || typeof play !== 'object') continue;
      log = appendEvent(log, formatGameEvent({
        type: 'CARD_PLAYED',
        payload: { actor_seat: play.seat, card_id: play.card_id, game_state_snapshot: snap },
      }, ctx));
    }
    log = appendEvent(log, formatGameEvent({
      type: 'TRICK_RESOLVED',
      payload: { winner_seat: trick.winner_seat, game_state_snapshot: snap },
    }, ctx));
  }
  for (const play of asList(snap.trick_plays)) {
    if (!play || typeof play !== 'object') continue;
    log = appendEvent(log, formatGameEvent({
      type: 'CARD_PLAYED',
      payload: { actor_seat: play.seat, card_id: play.card_id, game_state_snapshot: snap },
    }, ctx));
  }
  if (snap.hand_passed_out === true) {
    log = appendEvent(log, formatGameEvent({ type: 'HAND_PASSED_OUT', payload: {} }, ctx));
  }
  // The scored line only once the hand is over: score_summary can linger
  // from an earlier hand, so the state gates it, not the summary alone.
  const ss = snap.score_summary;
  const over = snap.current_state === 'score' || snap.current_state === 'end_of_hand';
  if (over && ss && ss.delta_team && typeof ss.delta_score === 'number') {
    log = appendEvent(log, formatGameEvent({
      type: 'HAND_SCORED',
      payload: { scoring_label: ss.label ?? 'hand complete', game_state_snapshot: snap },
    }, ctx));
  }
  return log;
}
